import type { FormConfig } from '../types';

/**
 * The complete inventory of forms we inject attribution into.
 *
 * Each form's `fields` map declares which Salesforce fields to populate.
 * The injector ships the full payload (attribution, first-touch, journey,
 * timestamps, page name, GA4 client ID, suite, UTMs) when consent is granted.
 *
 * If the resulting URL exceeds `urlBudget` (Formstack rejects past ~2000
 * chars), the injector degrades gracefully in this order:
 *   1. Drop `timestamps` (most verbose, lowest analytics value)
 *   2. Truncate `journey` to first + last 3 with " | ... | " between
 *   3. Drop `journey` entirely
 *   4. Drop `pageName`
 * Attribution_Type, First_Touch, UTMs, and Suite are always preserved.
 *
 * SFMC team reviews this file as a deploy artifact when field names change.
 */
export const FORM_REGISTRY: Record<string, FormConfig> = {
  'formstack:A0601': {
    id: 'formstack:A0601',
    selector: 'iframe[src*="formstack.io/A0601"]',
    baseUrl: 'https://formstack.io/A0601',
    description: 'Suite Rentals Contact Form',
    allowedOrigin: 'https://formstack.io',
    hasSuite: true,
    fields: {
      attribution: 'Formstack_Submissions__c.Attribution_Type__c',
      firstTouch: 'Formstack_Submissions__c.First_Touch__c',
      journey: 'Formstack_Submissions__c.Full_Attribution_Journey__c',
      timestamps: 'Formstack_Submissions__c.Journey_Timestamps__c',
      pageName: 'Formstack_Submissions__c.Page_Name__c',
      ga4ClientId: 'Formstack_Submissions__c.GA4_client_ID__c',
      suite: 'Formstack_Submissions__c.Event_Single_Suite__c',
      utmSource: 'Formstack_Submissions__c.UTM_Source__c',
      utmMedium: 'Formstack_Submissions__c.UTM_Medium__c',
      utmCampaign: 'Formstack_Submissions__c.UTM_Campaign__c',
      utmContent: 'Formstack_Submissions__c.UTM_Content__c',
      utmTerm: 'Formstack_Submissions__c.UTM_Term__c',
      utmId: 'Formstack_Submissions__c.UTM_ID__c',
    },
  },

  'formstack:ADB41': {
    id: 'formstack:ADB41',
    selector: 'iframe[src*="formstack.io/ADB41"]',
    baseUrl: 'https://formstack.io/ADB41',
    description: 'Boston Garden Society Contact Form',
    allowedOrigin: 'https://formstack.io',
    fields: {
      attribution: 'Formstack_Submissions__c.Attribution_Type__c',
      utmSource: 'Formstack_Submissions__c.UTM_Source__c',
      utmMedium: 'Formstack_Submissions__c.UTM_Medium__c',
      utmCampaign: 'Formstack_Submissions__c.UTM_Campaign__c',
      utmContent: 'Formstack_Submissions__c.UTM_Content__c',
      utmTerm: 'Formstack_Submissions__c.UTM_Term__c',
      utmId: 'Formstack_Submissions__c.UTM_ID__c',
    },
  },

  'formstack:A22ED': {
    id: 'formstack:A22ED',
    selector: 'iframe[src*="formstack.io/A22ED"]',
    baseUrl: 'https://formstack.io/A22ED',
    description: 'Holiday Gift Guide Contact Form',
    allowedOrigin: 'https://formstack.io',
    fields: {
      attribution: 'Formstack_Submissions__c.Attribution_Type__c',
      utmSource: 'Formstack_Submissions__c.UTM_Source__c',
      utmMedium: 'Formstack_Submissions__c.UTM_Medium__c',
      utmCampaign: 'Formstack_Submissions__c.UTM_Campaign__c',
    },
  },

  'tdgardenapps:newsletter': {
    id: 'tdgardenapps:newsletter',
    selector: 'iframe[src*="newsletter-form.html"]',
    baseUrl: 'https://tdgardenapps.com/bruins/form/newsletter-form.html',
    description: 'TD Garden Newsletter Signup',
    allowedOrigin: 'https://tdgardenapps.com',
    fields: {
      attribution: 'attribution',
      utmSource: 'utm_source',
      utmMedium: 'utm_medium',
      utmCampaign: 'utm_campaign',
    },
  },
};

export function getFormConfig(id: string): FormConfig | undefined {
  return FORM_REGISTRY[id];
}

export function allForms(): FormConfig[] {
  return Object.values(FORM_REGISTRY);
}

export function identifyFromUrl(url: string): FormConfig | null {
  for (const form of allForms()) {
    if (url.includes(form.baseUrl)) return form;
  }
  return null;
}
